import { BCMSCurrency, BCMSDate, BCMSDefaultModel, BCMSRecord, useGlobalController, useParams, useRoute, Workflow } from "@bcm-dev/bcms-vite";
import AddAddressForOvernightDeliveryPage from "compid/pages/AddAddressForOvernightDeliveryPage";
import { default as ConfirmAutopay } from "compid/pages/ConfirmAutoPay";
import PaymentConfirmation from "compid/pages/PaymentConfirmationPage";
import ReviewAndPay from "compid/pages/ReviewAndPayPage";
import { Account, Autopay, Bill, Biller, Payment } from "model/";

// Steps of workflow, key is the route of the step, value is the component
const steps = {
    "/reviewAndPay" : <ReviewAndPay />,
    "/paymentConfirmation" : <PaymentConfirmation />,
    "/autopayConfirmation" : <ConfirmAutopay />,
    "/addAddressForOvernightDelivery": <AddAddressForOvernightDeliveryPage />
};

/**
 * @function MakeAPaymentWorkflow
 * @returns {React.Component}
 * @description The main component for the multipay workflow
 * @captureState Example of changing state in a capture.
 *  document.dispatchEvent(new CustomEvent("force-workflow-state", {
        detail : {
            value : {
                payment: {
                    paymentAmount: 123,
                    paymentDate: 2,
                    paymentFrom: 1
                }
            }
        }
    }))
 */
export default function MakeAPaymentWorkflow(props) {
    const controller = useGlobalController();
    const { branding } = controller.state;

    // Param from URL provided by route determines which bill we are going to pay
    const {billID, stepID} = useParams();                                      
    const navigate = useRoute();

    const billExists = controller.state.bills.find(item => item.billID === billID) !== undefined;

    // Redirect to "pay bills" if there are no bills in model:
    if (controller.state.bills.length > 0 && billExists) {
        let value = getDefaultPayment(controller, billID);

        // Define the constructor function for forcing state through the 'force-workflow-state' event
        const constructor = record => {
            value = getDefaultPayment(controller, billID);

            // START: Set a fee amount for captures.
            const recordFeeAmount = record.payment ? record.payment.feeAmount : undefined;

            if(recordFeeAmount === undefined) {
                record.payment.feeAmount = Payment.getElectronicFee(branding);
            }
            // END: Set a fee amount for captures.

            const recordPaymentAsObj = { ...value.payment.record, ...record.payment };
            const recordPayment = Payment.init(recordPaymentAsObj);
            const mergedPayment = BCMSDefaultModel.mergeRecords(recordPayment, value.payment);
            const newPayment = Payment.init(mergedPayment);
            const paymentProps = { payment: newPayment };

            const recordBillAsObj = { ...value.bill.record, ...record.bill };
            const recordBill = Bill.init(recordBillAsObj);
            const mergedBill = BCMSDefaultModel.mergeRecords(recordBill, value.bill);
            const newBill = Bill.init(mergedBill);
            const billProps = { bill: newBill };

            // START: Select the fee date and set the "Payment" date to the fee date.
            // Needed for state captures since the fee date (with business offsets)
            // is info not accessible through the state captures.
            const setPaymentDateToFeeDate = record.setPaymentDateToFeeDate;
            if(setPaymentDateToFeeDate !== undefined) {
                const paymentType = value.payment.to.paymentType;
                paymentProps.payment.date = Payment.getFeeDate(paymentType);
            }
            // END: Select the fee date and set the "Payment" date to the fee date.
            // Needed for state captures since the fee date (with business offsets)
            // is info not accessible through the state captures.

            return { ...value, ...paymentProps, ...billProps };
        }

        return (<Workflow value={ value } steps={ Object.values(steps) } step={ stepID } constructor={ constructor } />);
    } else {
        navigate("/PayBills");
    }
}

// Return the steps of the workflow
export const getWorkflowSteps = () => steps;

// getDefaultPayment :: OBJECT -> OBJECT
// Return the default payment for the workflow
export const getDefaultPayment = (controller, billID) => {
    BCMSRecord.globalControllerFor([ Account, Payment, Biller, Bill, Autopay ], controller);

    const bill = billID === undefined 
        ? Bill.firstInstanceOf() 
        : Bill.initByID(billID) ?? Bill.firstInstanceOf();

    const payment = BCMSDefaultModel.payment();
    const autopay = BCMSDefaultModel.autopay();

    autopay.frequency = undefined;
    autopay.duration = undefined;
    autopay.numberOfPayments = "";
    autopay.id = Autopay.nextID();
    autopay.alwaysPay = BCMSCurrency.init(0);
    autopay.finalDeliveryBy = BCMSDate.init().offset(-1);
    autopay.billerID = bill.biller.id;

    payment.id = Payment.nextID();
    payment.to = bill.biller;
    // Only billers with a status of "due" should show a prefilled amount.
    // All other billers will show an amount of "$0.00" by default.
    payment.amount = bill.isDue ? bill.amount.amount ?? "0" : "0";
    // Set the default payment date depending on the payment type
    let paymentDate = payment.date;

    if(payment.to.paymentType === "paper") {
        Array.apply(null, Array(5)).forEach(i => paymentDate.nextBusinessDay());
    } else {
        if(Payment.hasRealTimeGoodFunds("electronic", controller.state.branding)) {
            paymentDate = Payment.getFeeDate("electronic", controller.state.branding);
            paymentDate.nextBusinessDay(); 
        } else {
            paymentDate.nextBusinessDay(); 
        }
         
    }

    payment.date = paymentDate;
    autopay.deliverBy = paymentDate;

    if(payment.to.eBillStatus === "Not Enrolled") { 
        payment.amount = "0";
    }
    
    bill.payment = payment;

    return {
        payment, bill, autopay, biller: bill.biller, 
        info: {
            address: "",
            city: "",
            state: "",
            zipFive: ""
        } 
    };
};

export const getSubmitMethod = (workflowStep, goToStep = 1) => evt => {
    let { payment, bill } = workflowStep.getStepState ?? {},
        payFromAccountRecord,
        withdrawnOnDate,
        hasImmediateWithdrawDate = false;

    const defaultPayment = BCMSDefaultModel.payment();
    const { branding } = workflowStep.model;

    payment = Payment.init(BCMSDefaultModel.mergeRecords(payment, defaultPayment));
    bill.status = "paid";

    const hasRealTimeGoodFunds = Payment.hasRealTimeGoodFunds(payment.to.paymentType, branding);
    const feeIsSelected = Payment.getIfDateHasFee(payment.date, payment.to.paymentType, branding);

    const isPaper = payment.to.paymentType === "paper";
    const isInstantPayment = payment.to.paymentType === "instantPayment";
    const isWithinMinutes = payment.to.paymentType === "withinMinutes";

    payFromAccountRecord = payment.from.record;
    withdrawnOnDate = Payment.getWithdrawOnDate(payment.date, payment.to.paymentType, branding);

    if (hasRealTimeGoodFunds && withdrawnOnDate.isToday()) { hasImmediateWithdrawDate = true; }

    const getFee = () => {
        if (feeIsSelected) {
            let options = branding.options ?? {},
                amount = isPaper
                    ? BCMSCurrency.init(options.overnight_check_fee_rxp22 === "undefined" ? "14.95" : options.overnight_check_fee_rxp22).amount
                    : BCMSCurrency.init(options.same_day_fee_rxp22 === "undefined" ? "9.95" : options.same_day_fee_rxp22).amount;

            if (isInstantPayment) { amount = BCMSCurrency.init(options.same_day_ip_fee_rxp22 === "Free" ? "0.00" : options.same_day_ip_fee_rxp22).amount; }
            if (isWithinMinutes) { amount = BCMSCurrency.init(options.within_minutes_ip_fee_rxp22 === "Free" ? "0.00" : options.within_minutes_ip_fee_rxp22).amount; }

            return amount;
        }

        return 0;
    }

    if (hasRealTimeGoodFunds && withdrawnOnDate.isToday()) {
        const { amount } = payment ?? {};

        let totalAmount = amount instanceof BCMSCurrency && !isNaN(amount.amount)
                ? parseFloat(amount.amount) + parseFloat(getFee())
                : parseFloat(defaultPayment.amount.amount) + parseFloat(getFee()),

            payFromAccountBalance = parseFloat(payFromAccountRecord.accountBalance);

        payFromAccountRecord = { ...payFromAccountRecord, accountBalance: payFromAccountBalance - totalAmount };
    }

    if (feeIsSelected) {
        payment.hasFee = true;
        payment.feeAmount = getFee();
    }

    payment.hasImmediateWithdrawDate = hasImmediateWithdrawDate;

    BCMSRecord.batch([
        Account.update({ ...payFromAccountRecord }),
        Payment.create(payment),
        Bill.update(bill)
    ], workflowStep.globalController);

    try {
        workflowStep.workflow.goStep(goToStep, { ...workflowStep.getStepState, payment, bill })
    } catch(error) {
        workflowStep.navigate(`/makeAPayment/${ bill.id }/${ workflowStep.currentStep + goToStep }`, { state: { ...workflowStep.getStepState, payment, bill } })
    }
}

export const getAutopaySubmitMethod = (workflowStep, goToStep = 2) => evt => {
    let { payment, bill, autopay } = workflowStep.getStepState ?? {},
        withdrawnOnDate,
        hasImmediateWithdrawDate = false;

    const defaultAutopay = BCMSDefaultModel.autopay();
    const defaultPayment = BCMSDefaultModel.payment();
    const { branding } = workflowStep.model;

    payment = Payment.init(BCMSDefaultModel.mergeRecords(payment, defaultPayment));
    bill.status = "paid";

    const hasRealTimeGoodFunds = Payment.hasRealTimeGoodFunds(payment.to.paymentType, branding);
    const feeIsSelected = Payment.getIfDateHasFee(payment.date, payment.to.paymentType, branding);

    const isPaper = payment.to.paymentType === "paper";
    const isInstantPayment = payment.to.paymentType === "instantPayment";
    const isWithinMinutes = payment.to.paymentType === "withinMinutes";

    withdrawnOnDate = Payment.getWithdrawOnDate(payment.date, payment.to.paymentType, branding);

    if (hasRealTimeGoodFunds && withdrawnOnDate.isToday()) { hasImmediateWithdrawDate = true; }

    const getFee = () => {
        if (feeIsSelected) {
            let options = branding.options ?? {},
                amount = isPaper
                    ? BCMSCurrency.init(options.overnight_check_fee_rxp22 === "undefined" ? "14.95" : options.overnight_check_fee_rxp22).amount
                    : BCMSCurrency.init(options.same_day_fee_rxp22 === "undefined" ? "9.95" : options.same_day_fee_rxp22).amount;

            if (isInstantPayment) { amount = BCMSCurrency.init(options.same_day_ip_fee_rxp22 === "Free" ? "0.00" : options.same_day_ip_fee_rxp22).amount; }
            if (isWithinMinutes) { amount = BCMSCurrency.init(options.within_minutes_ip_fee_rxp22 === "Free" ? "0.00" : options.within_minutes_ip_fee_rxp22).amount; }

            return amount;
        }

        return 0;
    }

    if (hasRealTimeGoodFunds && withdrawnOnDate.isToday()) {
        const { amount, from } = payment ?? {};

        let totalAmount = amount instanceof BCMSCurrency && !isNaN(amount.amount)
                ? parseFloat(amount.amount) + parseFloat(getFee())
                : parseFloat(defaultPayment.amount.amount) + parseFloat(getFee()),

            payFromAccountBalance = from.balance instanceof BCMSCurrency && !isNaN(from.balance.amount)
                ? parseFloat(from.balance.amount)
                : parseFloat(defaultPayment.from.balance.amount);

        payment.from.balance = payFromAccountBalance - totalAmount;
    }

    if (feeIsSelected) {
        payment.hasFee = true;
        payment.feeAmount = getFee();
    }

    payment.hasImmediateWithdrawDate = hasImmediateWithdrawDate;

    autopay.frequency = autopay.frequency !== undefined ? autopay.frequency : defaultAutopay.frequency;
    autopay.duration = autopay.duration !== undefined ? autopay.duration : defaultAutopay.duration;
    autopay.alwaysPay = payment.amount;

    autopay = Autopay.init(BCMSDefaultModel.mergeRecords(autopay, defaultAutopay));

    const numberOfPaymentsNumeric = parseFloat(autopay.numberOfPayments);
    if (numberOfPaymentsNumeric === 0) { autopay.numberOfPayments = defaultAutopay.numberOfPayments; }

    payment.autopay = autopay;
    payment.hasRelatedAutopay = true;

    BCMSRecord.batch([
        Payment.create(payment),
        Account.update(payment.from),
        Bill.update(bill),
        Autopay.create(autopay)
    ], workflowStep.globalController);

    try {
        workflowStep.workflow.goStep(goToStep, { ...workflowStep.getStepState, payment, bill, autopay });
    } catch(error) {
        workflowStep.navigate(`/makeAPayment/${ autopay.id }/${ workflowStep.currentStep + goToStep }`, { state: { ...workflowStep.getStepState, payment, bill, autopay } })
    }
}